import {
  fetchDesignationOptionsRequest,
  finalizeRegistrationRequest,
  sendAccountLinkingEmailRequest,
} from '@/requests';
import {
  AnalyticsService,
  EVENTS,
  parseUserTags,
  parseUserTagsForGTM,
  setGtmUserProperties,
} from '@/services';
import { DesignationOption } from '@/types';
import { camelCaseKeys } from '@/utils';

import { postgrestApi } from '../postgrest-api';
import { PostgrestApiTags } from '../store.constants';
import { handleOnQueryStartedError } from '../store.utils';
import { userApiPostgrest } from '../user-api/user-api.slice';

export const onboardingApiPostgrest = postgrestApi.injectEndpoints({
  endpoints: builder => ({
    fetchDesignationOptions: builder.query<DesignationOption[], void>({
      query: fetchDesignationOptionsRequest,
      transformResponse: (response: unknown[]) =>
        response.map(camelCaseKeys<DesignationOption>),
    }),
    sendAccountLinkingEmail: builder.mutation<VoidFunction, { email: string }>({
      query: sendAccountLinkingEmailRequest,
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          AnalyticsService.logEvent(EVENTS.ONBOARDING_EVENTS.RESEND_B2B.INTENT);
          await queryFulfilled;
          AnalyticsService.logEvent(
            EVENTS.ONBOARDING_EVENTS.RESEND_B2B.SUCCESS,
          );
        } catch (error: any) {
          AnalyticsService.logEvent(EVENTS.ONBOARDING_EVENTS.RESEND_B2B.FAIL, {
            error: error?.message,
          });
          handleOnQueryStartedError(error);
        }
      },
    }),
    completeOnboarding: builder.mutation<unknown, void>({
      query: finalizeRegistrationRequest,
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          AnalyticsService.logEvent(EVENTS.ONBOARDING_EVENTS.FINALIZE.INTENT);
          await queryFulfilled;

          const { data: userTags } = await dispatch(
            userApiPostgrest.endpoints.fetchUserTags.initiate(undefined, {
              forceRefetch: true,
              subscribe: false,
            }),
          );

          AnalyticsService.logEvent(EVENTS.ONBOARDING_EVENTS.FINALIZE.SUCCESS, {
            ...parseUserTags(userTags),
          });
          // needs to be set before login event to allow GTM tags to trigger based on userTags
          setGtmUserProperties({ userTags: parseUserTagsForGTM(userTags) });
          AnalyticsService.logEvent(EVENTS.GA4_KEY_EVENTS.SIGN_UP);
        } catch (error: any) {
          AnalyticsService.logEvent(EVENTS.ONBOARDING_EVENTS.FINALIZE.FAIL, {
            error: error?.data?.message,
          });
          handleOnQueryStartedError(error);
        }
      },
      invalidatesTags: [
        PostgrestApiTags.PLAN,
        PostgrestApiTags.PLAYER_CONFIG,
        PostgrestApiTags.USER_REGISTRATION_STATE,
        PostgrestApiTags.USER_PROFILE,
        PostgrestApiTags.USER_REGION,
        PostgrestApiTags.CONTENT_PREFERENCES,
      ],
    }),
  }),
});

export const {
  useFetchDesignationOptionsQuery,
  useSendAccountLinkingEmailMutation,
  useCompleteOnboardingMutation,
} = onboardingApiPostgrest;
